import React, { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { FiCheck, FiX } from 'react-icons/fi';
import { AiOutlineDelete } from 'react-icons/ai';
import { DataTable } from '@components/data-table';
import { TooltipTop } from '@components/tooltips';
import { AirflowDagRun } from '../../types';
import { DagRunStateBadge, formatDagVersion, formatDateTime, formatDurationSeconds, RunTypeLabel } from '../../utils';
import styles from '../../styles.module.scss';

type RunHistoryTableProps = {
    runs: AirflowDagRun[];
    isLoading: boolean;
    onRunClick: (run: AirflowDagRun) => void;
    onMarkSuccess: (run: AirflowDagRun) => void;
    onMarkFailed: (run: AirflowDagRun) => void;
    onDeleteRequest: (run: AirflowDagRun) => void;
};

/*
 * Server-sorted run history table (sort is controlled by the tab's Select, not column click)
 */
export function RunHistoryTable({ runs, isLoading, onRunClick, onMarkSuccess, onMarkFailed, onDeleteRequest }: RunHistoryTableProps) {
    const columns = useMemo<ColumnDef<AirflowDagRun>[]>(
        () => [
            { id: 'state', header: 'State', enableSorting: false, cell: ({ row }) => <DagRunStateBadge state={row.original.state} /> },
            { id: 'run_after', header: 'Run After', enableSorting: false, cell: ({ row }) => formatDateTime(row.original.execution_date ?? row.original.run_after) },
            { id: 'dag_run_id', header: 'Run ID', enableSorting: false, cell: ({ row }) => <span style={{ fontFamily: 'monospace', color: '#fcad01' }}>{row.original.dag_run_id}</span> },
            { id: 'run_type', header: 'Run Type', enableSorting: false, cell: ({ row }) => <RunTypeLabel runType={row.original.run_type} /> },
            { id: 'triggering_user_name', header: 'Triggering User', enableSorting: false, cell: ({ row }) => row.original.triggering_user_name ?? row.original.triggered_by ?? '—' },
            { id: 'start_date', header: 'Start Date', enableSorting: false, cell: ({ row }) => formatDateTime(row.original.start_date) },
            { id: 'duration', header: 'Duration', enableSorting: false, cell: ({ row }) => formatDurationSeconds(row.original.duration) },
            { id: 'dag_version', header: 'DAG Version', enableSorting: false, cell: ({ row }) => formatDagVersion(row.original.dag_versions) },
            {
                id: 'actions',
                header: 'Actions',
                enableSorting: false,
                cell: ({ row }) => {
                    const run = row.original;
                    return (
                        <span style={{ display: 'flex', gap: 12 }}>
                            <TooltipTop title="Mark Success">
                                <span
                                    className={styles.actionIcon}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onMarkSuccess(run);
                                    }}
                                >
                                    <FiCheck />
                                </span>
                            </TooltipTop>
                            <TooltipTop title="Mark Failed">
                                <span
                                    className={styles.actionIcon}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onMarkFailed(run);
                                    }}
                                >
                                    <FiX />
                                </span>
                            </TooltipTop>
                            <TooltipTop title="Delete">
                                <span
                                    className={`${styles.actionIcon} ${styles.destructive}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDeleteRequest(run);
                                    }}
                                >
                                    <AiOutlineDelete />
                                </span>
                            </TooltipTop>
                        </span>
                    );
                },
            },
        ],
        [onMarkSuccess, onMarkFailed, onDeleteRequest]
    );

    return (
        <DataTable
            data={runs}
            columns={columns}
            isLoading={isLoading}
            onRowClick={onRunClick}
            rowKey={(run) => run.dag_run_id}
            emptyText="No runs found"
            maxHeight={400}
        />
    );
}
